import { useState } from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { testExternalPricingEngineConnection } from '../../api/pricingSettingsApi';
import { usePricingSettings, useUpdatePricingSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { EngineMode, UpdatePricingSettingsRequest } from '../../model/settingsTypes';
import {
  FeedbackSnackbar,
  FieldRow,
  MaskedCredentialField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import { ENGINE_MODE_OPTIONS } from '../options';

/** Pricing / promotion engine configuration and external-engine connection. */
export function PricingSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('pricing');
  const query = usePricingSettings();
  const update = useUpdatePricingSettings();
  const form = useSettingsForm(query.data);
  const [engineSecret, setEngineSecret] = useState('');
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const dirty = form.dirty || engineSecret.trim() !== '';

  const reset = () => {
    form.reset();
    setEngineSecret('');
  };

  const handleSave = () => {
    const draft = form.draft;
    if (!draft) {
      return;
    }
    const body: UpdatePricingSettingsRequest = {
      version: draft.version,
      pricingEngineMode: draft.pricingEngineMode,
      promotionEngineMode: draft.promotionEngineMode,
      roundingRule: draft.roundingRule,
      discountStacking: draft.discountStacking,
      promoCodesEnabled: draft.promoCodesEnabled,
      deliveryPriceCalculation: draft.deliveryPriceCalculation,
      paymentFeeCalculation: draft.paymentFeeCalculation,
      externalEngine: {
        baseUrl: draft.externalEngine?.baseUrl ?? null,
        ...(engineSecret.trim() !== '' ? { secret: engineSecret } : {}),
      },
    };
    update.mutate(body, {
      onSuccess: () => {
        setEngineSecret('');
        success(t('pricingSettings.savedFeedback'));
      },
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('pricingSettings.title')}
      subtitle={t('pricingSettings.subtitle')}
      icon={<SellRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('pricingSettings.enginesSection.title')} icon={<SellRoundedIcon />}>
              <SelectField
                label={t('pricingSettings.enginesSection.pricingEngineModeLabel')}
                value={draft.pricingEngineMode}
                options={ENGINE_MODE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('pricingEngineMode', value as EngineMode)}
                disabled={disabled}
              />
              <SelectField
                label={t('pricingSettings.enginesSection.promotionEngineModeLabel')}
                value={draft.promotionEngineMode}
                options={ENGINE_MODE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
                onChange={(value) => form.setField('promotionEngineMode', value as EngineMode)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('pricingSettings.calculationSection.title')} icon={<TuneRoundedIcon />}>
              <TextInputField
                label={t('pricingSettings.calculationSection.roundingRuleLabel')}
                value={draft.roundingRule}
                onChange={(value) => form.setField('roundingRule', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('pricingSettings.calculationSection.discountStackingLabel')}
                description={t('pricingSettings.calculationSection.discountStackingDescription')}
                checked={draft.discountStacking}
                onChange={(value) => form.setField('discountStacking', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('pricingSettings.calculationSection.promoCodesLabel')}
                checked={draft.promoCodesEnabled}
                onChange={(value) => form.setField('promoCodesEnabled', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('pricingSettings.calculationSection.deliveryPriceCalculationLabel')}
                value={draft.deliveryPriceCalculation}
                onChange={(value) => form.setField('deliveryPriceCalculation', value)}
                disabled={disabled}
              />
              <TextInputField
                label={t('pricingSettings.calculationSection.paymentFeeCalculationLabel')}
                value={draft.paymentFeeCalculation}
                onChange={(value) => form.setField('paymentFeeCalculation', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard
              title={t('pricingSettings.externalEngineSection.title')}
              description={t('pricingSettings.externalEngineSection.description')}
              icon={<HubRoundedIcon />}
            >
              <TextInputField
                label={t('pricingSettings.externalEngineSection.baseUrlLabel')}
                type="url"
                value={draft.externalEngine?.baseUrl ?? ''}
                onChange={(value) =>
                  form.setField('externalEngine', {
                    baseUrl: value || null,
                    credential: draft.externalEngine?.credential ?? null,
                  })
                }
                disabled={disabled}
                placeholder="https://pricing.example.com"
              />
              <MaskedCredentialField
                label={t('pricingSettings.externalEngineSection.credentialLabel')}
                description={t('pricingSettings.externalEngineSection.credentialDescription')}
                credential={draft.externalEngine?.credential ?? null}
                secret={engineSecret}
                onSecretChange={setEngineSecret}
                disabled={!canManageCredentials}
              />
              <FieldRow
                label={t('pricingSettings.externalEngineSection.connectionLabel')}
                description={t('pricingSettings.externalEngineSection.connectionDescription')}
                alignTop
              >
                <TestConnectionButton
                  run={testExternalPricingEngineConnection}
                  disabled={!canManage}
                />
              </FieldRow>
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
